import React, { useEffect } from 'react';
import { Info } from 'lucide-react';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Switch } from '@/components/ui/switch';
import { Typography } from '@/components/ui/typography';
import { useOrgWatermarkAvailable } from '@/hooks/useOrgWatermarkAvailable';
import type {
  ConfigurationSettingsState,
  SettingToggleType,
} from '../../../types/pms.types';

interface ConfigurationSettingProps {
  handleChange: (type: SettingToggleType) => void;
  editedResponse: ConfigurationSettingsState;
  setEditedResponse: React.Dispatch<
    React.SetStateAction<ConfigurationSettingsState>
  >;
  isDisabled?: boolean;
  pmsSourceName?: string;
}

const ConfigurationSetting: React.FC<ConfigurationSettingProps> = ({
  handleChange,
  editedResponse,
  setEditedResponse,
  isDisabled,
  pmsSourceName,
}) => {
  const watermarkAvailable = useOrgWatermarkAvailable();

  // Watermark sync only makes sense when auto-sync is on AND the org has a
  // watermark on file; force it off otherwise so we never persist a watermark
  // sync the backend can't fulfill (DV-1958).
  useEffect(() => {
    if (!editedResponse.autoSyncAssets || !watermarkAvailable) {
      setEditedResponse({
        ...editedResponse,
        watermarkedPhotos: false,
      });
    }
  }, [editedResponse.autoSyncAssets, watermarkAvailable]);

  return (
    <div className="px-4">
      <div className="mb-5 mt-6 border-t border-border" />
      <div className="pb-3">
        <Typography variant="heading-md">Configuration Settings</Typography>
      </div>
      <div className="flex flex-col gap-4">

        {/* Auto-Sync Assets + its nested Watermarked Photos row share one flex item:
            the collapsed row (maxHeight 0) must not contribute gap-4 on both sides. */}
        <div>
          {/* Auto-Sync Assets */}
          <div>
            <Typography variant="body-semibold" className="font-bold">
              Auto-Sync Assets
            </Typography>
            <div className="flex items-center justify-between">
              <Typography variant="body-medium">
                As soon as the assets are ready, PlanOmatic will sync them directly
                to {pmsSourceName}
              </Typography>
              <Switch
                checked={editedResponse.autoSyncAssets}
                onCheckedChange={() => handleChange('autoSync')}
                disabled={isDisabled}
                aria-label="Auto-Sync Assets"
              />
            </div>
          </div>

          {/* Watermarked Photos (nested under auto-sync) */}
          <div
            className="overflow-hidden transition-all duration-500"
            style={{
              maxHeight: editedResponse.autoSyncAssets ? '60px' : '0',
            }}
          >
            <div className="ml-6 mt-2">
              <Typography variant="body-semibold" className="font-bold">
                Watermarked Photos
              </Typography>
            </div>
            <div className="ml-6 flex items-center justify-between">
              <Typography variant="body-medium">
                The watermarked version of the photos will be used in the auto-sync
              </Typography>
              <div className="flex items-center gap-1">
                {!watermarkAvailable && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          aria-label="Watermark Unavailable — Contact Support"
                          className="text-muted-foreground hover:text-foreground cursor-help"
                        >
                          <Info className="h-5 w-5" aria-hidden="true" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        No watermark image is on file. Please contact{' '}
                        <a
                          href="mailto:support@planomatic.com"
                          className="text-primary underline underline-offset-2"
                        >
                          support@planomatic.com
                        </a>{' '}
                        to set up a watermark
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                <Switch
                  checked={editedResponse.watermarkedPhotos}
                  onCheckedChange={() => handleChange('waterMarkPhotos')}
                  disabled={
                    isDisabled || !editedResponse.autoSyncAssets || !watermarkAvailable
                  }
                  aria-label="Use Watermarked Photos"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfigurationSetting;
